import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { request } from "../../../../utils/Axios";
import { UserType } from "../../../types/user.types";
import { RootState } from "../../store";
import {
  toastError,
  toastSuccess,
} from "../../../../components/CustomToastify";

interface InitState {
  status: "idle" | "success" | "loading" | "fail";
  notificationStatus: "idle" | "success" | "loading" | "fail";
  data: Array<UserType>;
  notifications: any[];
  count: number;
}

const initState: InitState = {
  status: "idle",
  notificationStatus: "idle",
  data: [],
  notifications: [],
  count: 0,
};

export const fetchUserNotifications = createAsyncThunk(
  "users/fetchUserNotifications",
  async () => {
    // async (query: { limit: number; skip: number }) => {
    const { data } = await request.get(`/api/v1/notification`);
    return data;
  }
);

export const deleteNotifications = createAsyncThunk(
  "users/deleteNotifications",
  async (id: string) => {
    const { data } = await request.delete(`/api/v1/notification/${id}`);
    return data;
  }
);

export const markUserNotificationsAsRead = createAsyncThunk(
  "users/markUserNotificationsAsRead",
  async (ids: string[]) => {
    const { data } = await request.post(`/api/v1/notification/read`, [ids]);
    return data;
  }
);

export const fetchAllUsers = createAsyncThunk(
  "users/fetchAllUsers",
  async (query: { limit: number; skip: number }) => {
    const { data } = await request.get(
      `/api/v1/user?skip=${query.skip}&limit=${query.limit}`
    );
    return data;
  }
);

export const deleteUser = createAsyncThunk(
  "users/deleteUser",
  async (req: { email: string }) => {
    const { data } = await request.delete(`/api/v1/user/delete/${req.email}`);
    return data;
  }
);

export const searchUsers = createAsyncThunk(
  "users/searchUsers",
  async (query: { term: string; skip?: number; limit?: number }) => {
    const { data } = await request.get(
      `/api/v1/user/search?term=${query.term}&skip=${query.skip || 0}&limit=${
        query.limit || 20
      }`
    );
    return data;
  }
);

const userSlice = createSlice({
  name: "users",
  initialState: initState,
  reducers: {
    clearUsers: (state) => {
      state.data = [];
      state.count = 0;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchAllUsers.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.status = "success";
        state.data = [...state.data, ...action.payload.body.data];
        state.count = action.payload.body.count;
      })
      .addCase(fetchAllUsers.rejected, (state) => {
        state.status = "fail";
      });
    builder
      .addCase(searchUsers.pending, (state) => {
        state.status = "loading";
      })
      .addCase(searchUsers.fulfilled, (state, action) => {
        state.status = "success";
        state.data = [...action.payload.body.data];
        state.count = action.payload.body.count;
      })
      .addCase(searchUsers.rejected, (state) => {
        state.status = "fail";
      });
    builder
      .addCase(fetchUserNotifications.pending, (state) => {
        state.notificationStatus = "loading";
      })
      .addCase(fetchUserNotifications.fulfilled, (state, action) => {
        state.notificationStatus = "success";
        state.notifications = [...state.notifications, ...action.payload.body];
      })
      .addCase(fetchUserNotifications.rejected, (state) => {
        state.notificationStatus = "fail";
      });
    builder
      .addCase(deleteNotifications.pending, (state) => {
        state.notificationStatus = "loading";
      })
      .addCase(deleteNotifications.fulfilled, (state, action) => {
        state.notificationStatus = "success";
        state.notifications = state.notifications.filter(
          (notification) => notification._id !== action.payload._id
        );
      })
      .addCase(deleteNotifications.rejected, (state) => {
        state.notificationStatus = "fail";
      });
    builder
      .addCase(deleteUser.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.status = "success";
        state.data = state.data.filter(
          (user) => user._id !== action.payload._id
        );
        toastSuccess("User deleted successfully!");
      })
      .addCase(deleteUser.rejected, (state) => {
        state.status = "fail";
        toastError("Failed to delete user!");
      });
  },
});

export const { clearUsers } = userSlice.actions;
export default userSlice.reducer;
export const selectAllUsers = (state: RootState) => state.users.data;
export const selectAllUserNotifications = (state: RootState) =>
  state.users.notifications;
export const selectAllUserNotificationStatus = (state: RootState) =>
  state.users.notificationStatus;
export const selectUsersCount = (state: RootState) => state.users.count;
